import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Check, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Spinner from '@/components/ui/Spinner';
import type { Project } from '@/hooks/useProject';
import type { Readiness } from '@/lib/projectReadiness';
import { cn } from '@/lib/utils';

type StepState = 'done' | 'current' | 'todo' | 'failed';

interface Step {
  key: string;
  state: StepState;
  optional?: boolean;
  action?: { label: string; onClick: () => void; busy?: boolean };
}

function StepMarker({ state, index }: { state: StepState; index: number }) {
  return (
    <span
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 font-mono text-[11px]',
        state === 'done' && 'border-success bg-success text-success-foreground',
        state === 'failed' && 'border-destructive bg-destructive text-destructive-foreground',
        state === 'current' && 'border-primary text-primary ring-[3px] ring-primary/15',
        state === 'todo' && 'border-track text-faint',
      )}
    >
      {state === 'done' ? (
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      ) : state === 'failed' ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        index
      )}
    </span>
  );
}

/**
 * The path from an empty project to a runnable one.
 *
 * The disabled "New Production" button only ever names the CURRENT blocker, so you
 * discover the requirements one refusal at a time and never see how many are left.
 * This lays the whole sequence out, marks where you are, and puts the next action on
 * the step that needs it.
 *
 * Once the project is runnable it shrinks to a single confirmation line rather than
 * disappearing — vanishing UI leaves people unsure whether they finished or broke
 * something.
 */
export default function SetupChecklist({
  project,
  readiness,
  generating,
  uploading,
  onGenerateBible,
  onUploadScript,
  onUploadBrief,
  onGoToSettings,
}: {
  project: Project;
  readiness: Readiness;
  generating: boolean;
  uploading: boolean;
  onGenerateBible: () => void;
  onUploadScript: (file: File) => void;
  onUploadBrief: (file: File) => void;
  onGoToSettings: () => void;
}) {
  const { t } = useTranslation(['dashboard', 'common']);
  const scriptRef = useRef<HTMLInputElement>(null);
  const briefRef = useRef<HTMLInputElement>(null);
  const isFootage = (project.production_type || 'generative') === 'footage';

  const hasBrief = !!(project.creative_brief || project.creative_brief_preview);
  const briefStep = {
    state: (hasBrief ? 'done' : 'todo') as StepState,
    optional: true,
    action: hasBrief ? undefined : { label: t('setup.addBrief'), onClick: () => briefRef.current?.click() },
  };

  const steps: Step[] = isFootage
    ? [
        {
          key: 'collection',
          state: project.iconik_collection_id?.trim() ? 'done' : 'current',
          action: project.iconik_collection_id?.trim() ? undefined : { label: t('setup.goToSettings'), onClick: onGoToSettings },
        },
        { key: 'brief', ...briefStep },
      ]
    : [
        {
          key: 'script',
          state: project.source_script?.trim() ? 'done' : 'current',
          // Opens the picker directly. It used to just switch to the Source
          // section — which is the DEFAULT section, so the button did nothing at
          // all on first load.
          action: project.source_script?.trim()
            ? undefined
            : { label: t('setup.goToSource'), onClick: () => scriptRef.current?.click(), busy: uploading },
        },
        {
          key: 'bible',
          state: project.script?.trim()
            ? 'done'
            : project.bible_status === 'failed'
              ? 'failed'
              : project.source_script?.trim()
                ? 'current'
                : 'todo',
          action:
            !project.script?.trim() && project.source_script?.trim()
              ? { label: t('setup.generateBible'), onClick: onGenerateBible, busy: generating }
              : undefined,
        },
        { key: 'brief', ...briefStep },
      ];

  if (readiness.ready) {
    return (
      <Card className="border-success/40 bg-success/8">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-2.5 text-[13px]">
            <Sparkles className="h-4 w-4 shrink-0 text-success" />
            <span className="font-medium">{t('setup.readyTitle')}</span>
            <span className="text-muted-foreground">{t('setup.readyBody')}</span>
          </div>
          {/* Becoming ready shouldn't silently drop the one recommended step you
              skipped, at exactly the moment you'd act on it. */}
          {!hasBrief && (
            <p className="m-0 mt-1.5 pl-[26px] text-[11.5px] leading-relaxed text-faint">{t('setup.readyBriefNudge')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-5">
        <input
          ref={scriptRef}
          type="file"
          accept=".txt,text/plain"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onUploadScript(file);
          }}
        />
        <input
          ref={briefRef}
          type="file"
          accept=".txt,.md,text/plain,text/markdown"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onUploadBrief(file);
          }}
        />
        <h3 className="m-0 font-display text-[15px] font-semibold">{t('setup.title')}</h3>
        <p className="mb-4 mt-1 text-[12.5px] leading-relaxed text-faint">{t('setup.subtitle')}</p>

        <ol className="m-0 list-none space-y-3 p-0">
          {steps.map((step, i) => (
            <li key={step.key} className="flex items-start gap-3">
              <StepMarker state={step.state} index={i + 1} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn('text-[13px] font-medium', step.state === 'todo' ? 'text-muted-foreground' : 'text-foreground')}
                  >
                    {t(`setup.step.${step.key}`)}
                  </span>
                  {step.optional && (
                    <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-faint">
                      {t('setup.recommended')}
                    </span>
                  )}
                </div>
                <p className="m-0 mt-0.5 text-[11.5px] leading-relaxed text-faint">
                  {step.state === 'failed' ? t('setup.step.bibleFailed') : t(`setup.step.${step.key}Hint`)}
                </p>
              </div>
              {step.action && (
                <Button size="sm" variant="outline" disabled={step.action.busy} onClick={step.action.onClick}>
                  {step.action.busy ? <Spinner className="mr-1.5" /> : null}
                  {step.action.busy ? t('setup.working') : step.action.label}
                  {!step.action.busy && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
                </Button>
              )}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
